import React, { useEffect, useMemo, useState } from "react";
import { api, formatValue } from "@/lib/api";
import { useFormat } from "@/contexts/FormatContext";
import { Loader2, Play, RotateCcw, Trophy, Search } from "lucide-react";
import DmeTag from "@/components/DmeTag";
import FormatToggle from "@/components/FormatToggle";

const TEAM_OPTS = [8, 10, 12];

export default function MockDraft() {
  const { format } = useFormat();
  const [pool, setPool] = useState("rookies"); // rookies | startup
  const [teams, setTeams] = useState(12);
  const [rounds, setRounds] = useState(4);
  const [slot, setSlot] = useState(1);
  const [loading, setLoading] = useState(false);

  const [available, setAvailable] = useState([]);
  const [picks, setPicks] = useState([]);   // {overall, round, teamIdx, player}
  const [cursor, setCursor] = useState(-1);  // overall pick index, -1 = not started
  const [q, setQ] = useState("");

  const totalPicks = teams * rounds;
  const userTeam = slot - 1;

  // Snake order → which team picks at a given overall index
  const teamForPick = (overall) => {
    const rnd = Math.floor(overall / teams);
    const inRound = overall % teams;
    return rnd % 2 === 0 ? inRound : teams - 1 - inRound;
  };

  const fetchPool = async () => {
    if (pool === "rookies") {
      const r = await api.get("/draft/board");
      return (r.data.rookies || []).map(p => ({ player_id: p.player_id, name: p.name, position: p.position, team: p.team, value: p.value }));
    }
    const r = await api.get("/players?limit=300");
    return (r.data.players || []).filter(p => ["QB", "RB", "WR", "TE"].includes(p.position))
      .map(p => ({ player_id: p.player_id, name: p.name, position: p.position, team: p.team, value: p.value }));
  };

  const start = async () => {
    setLoading(true);
    try {
      const list = (await fetchPool()).sort((a, b) => b.value - a.value);
      setAvailable(list);
      setPicks([]);
      setCursor(0);
    } finally { setLoading(false); }
  };

  const reset = () => { setCursor(-1); setPicks([]); setAvailable([]); setQ(""); };

  // CPU best-available with slight randomness among the top few
  const cpuPick = (list) => {
    const top = list.slice(0, 3);
    return top[Math.floor(Math.random() * top.length)] || list[0];
  };

  // Advance through CPU picks until it's the user's turn or the draft ends.
  useEffect(() => {
    if (cursor < 0 || cursor >= totalPicks || available.length === 0) return;
    if (teamForPick(cursor) === userTeam) return; // wait for user
    const t = setTimeout(() => {
      const player = cpuPick(available);
      if (!player) return;
      setPicks(prev => [...prev, { overall: cursor, round: Math.floor(cursor / teams), teamIdx: teamForPick(cursor), player }]);
      setAvailable(prev => prev.filter(p => p.player_id !== player.player_id));
      setCursor(c => c + 1);
    }, 280);
    return () => clearTimeout(t);
  }, [cursor, available]); // eslint-disable-line

  const userPick = (player) => {
    if (teamForPick(cursor) !== userTeam) return;
    setPicks(prev => [...prev, { overall: cursor, round: Math.floor(cursor / teams), teamIdx: userTeam, player }]);
    setAvailable(prev => prev.filter(p => p.player_id !== player.player_id));
    setCursor(c => c + 1);
    setQ("");
  };

  const started = cursor >= 0;
  const done = started && cursor >= totalPicks;
  const onClock = started && !done ? teamForPick(cursor) : null;
  const isUserTurn = onClock === userTeam;

  const filtered = useMemo(() => {
    if (!q) return available.slice(0, 40);
    const ql = q.toLowerCase();
    return available.filter(p => p.name.toLowerCase().includes(ql)).slice(0, 40);
  }, [q, available]);

  const myRoster = picks.filter(p => p.teamIdx === userTeam);
  const byTeamRound = (teamIdx, rnd) => picks.find(p => p.teamIdx === teamIdx && p.round === rnd);

  return (
    <div className="space-y-6 fade-up">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[var(--accent)] text-xs tracking-widest mb-1">// SIMULATOR</div>
          <h1 className="font-display text-4xl font-bold" data-testid="mock-heading">Mock Draft</h1>
          <p className="text-[var(--text-2)] mt-2">Draft against value-based CPUs. Snake order, live in your current format.</p>
        </div>
        {started && (
          <button onClick={reset} className="btn-secondary text-sm inline-flex items-center gap-2" data-testid="mock-reset"><RotateCcw className="w-4 h-4" /> New draft</button>
        )}
      </header>

      {!started ? (
        <div className="card-bordered p-6 max-w-xl space-y-5" data-testid="mock-setup">
          <div>
            <div className="text-xs tracking-widest text-[var(--text-3)] mb-2">DRAFT POOL</div>
            <div className="inline-flex border border-[var(--border)] rounded-md overflow-hidden">
              {["rookies", "startup"].map(p => (
                <button key={p} onClick={() => setPool(p)} className={`px-4 py-2 text-sm capitalize ${pool === p ? "bg-[var(--accent)] text-black" : "text-[var(--text-2)] hover:bg-white/5"}`} data-testid={`mock-pool-${p}`}>{p === "rookies" ? "Rookie Draft" : "Startup (all)"}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <label className="text-sm">
              <div className="text-xs tracking-widest text-[var(--text-3)] mb-1">TEAMS</div>
              <select value={teams} onChange={e => { const t = +e.target.value; setTeams(t); if (slot > t) setSlot(t); }} className="input-dark" data-testid="mock-teams">
                {TEAM_OPTS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label className="text-sm">
              <div className="text-xs tracking-widest text-[var(--text-3)] mb-1">ROUNDS</div>
              <select value={rounds} onChange={e => setRounds(+e.target.value)} className="input-dark" data-testid="mock-rounds">
                {[1, 2, 3, 4, 5, 6].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <label className="text-sm">
              <div className="text-xs tracking-widest text-[var(--text-3)] mb-1">YOUR PICK</div>
              <select value={slot} onChange={e => setSlot(+e.target.value)} className="input-dark" data-testid="mock-slot">
                {Array.from({ length: teams }, (_, i) => i + 1).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <button onClick={start} disabled={loading} className="btn-primary inline-flex items-center gap-2" data-testid="mock-start">
              {loading ? <Loader2 className="w-4 h-4 spin" /> : <Play className="w-4 h-4" />} Start draft
            </button>
            <div className="flex items-center gap-2">
              <span className="text-[10px] tracking-widest text-[var(--text-3)] font-mono">VALUE FORMAT</span>
              <FormatToggle />
            </div>
          </div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <div className="card-bordered p-3 flex items-center justify-between" data-testid="mock-status">
              {done ? (
                <span className="font-display font-semibold inline-flex items-center gap-2"><Trophy className="w-4 h-4 text-[var(--accent)]" /> Draft complete</span>
              ) : isUserTurn ? (
                <span className="font-display font-semibold text-[var(--accent)]">You're on the clock — Pick {cursor + 1} (Round {Math.floor(cursor / teams) + 1})</span>
              ) : (
                <span className="inline-flex items-center gap-2 text-[var(--text-2)]"><Loader2 className="w-4 h-4 spin" /> Team {onClock + 1} drafting…</span>
              )}
            </div>

            <div className="card-bordered overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] tracking-widest text-[var(--text-3)] border-b border-[var(--border)]">
                    <th className="p-2 text-left">RD</th>
                    {Array.from({ length: teams }, (_, i) => (
                      <th key={i} className={`p-2 text-left ${i === userTeam ? "text-[var(--accent)]" : ""}`}>T{i + 1}{i === userTeam ? "★" : ""}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: rounds }, (_, r) => (
                    <tr key={r} className="border-b border-[var(--border)] last:border-0">
                      <td className="p-2 text-[var(--text-3)]">{r + 1}</td>
                      {Array.from({ length: teams }, (_, c) => {
                        const pk = byTeamRound(c, r);
                        return (
                          <td key={c} className={`p-2 ${c === userTeam ? "bg-[var(--accent)]/5" : ""}`}>
                            {pk ? <span className="block leading-tight">{pk.player.name}<span className="block text-[10px] text-[var(--text-3)]">{pk.player.position}</span></span> : <span className="text-[var(--text-3)]">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-4">
            {isUserTurn && !done && (
              <div className="card-bordered p-3" data-testid="mock-pick-panel">
                <div className="relative mb-2">
                  <Search className="w-4 h-4 absolute left-2 top-2.5 text-[var(--text-3)]" />
                  <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search best available…" className="input-dark w-full !pl-10" data-testid="mock-search" />
                </div>
                <div className="max-h-80 overflow-y-auto space-y-1">
                  {filtered.map(p => (
                    <button key={p.player_id} onClick={() => userPick(p)} className="w-full flex justify-between items-center p-2 rounded hover:bg-white/5 text-sm" data-testid={`mock-available-${p.player_id}`}>
                      <span>{p.name} <span className="text-[var(--text-3)] text-xs">· {p.position} {p.team}</span></span>
                      <span className="tabular text-[var(--accent)]">{formatValue(p.value)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="card-bordered p-3">
              <div className="text-xs tracking-widest text-[var(--text-3)] mb-2">YOUR ROSTER</div>
              <div className="space-y-1" data-testid="mock-my-roster">
                {myRoster.map((p, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span>{p.player.name} <span className="text-[var(--text-3)] text-xs">· {p.player.position}</span></span>
                    <span className="tabular text-[var(--accent)]">{formatValue(p.player.value)}</span>
                  </div>
                ))}
                {myRoster.length === 0 && <div className="text-xs text-[var(--text-3)]">No picks yet.</div>}
                {myRoster.length > 0 && (
                  <div className="flex justify-between text-sm border-t border-[var(--border)] pt-2 mt-2 font-semibold">
                    <span className="inline-flex items-center gap-1.5">Total <DmeTag /></span>
                    <span className="tabular text-[var(--accent)]">{formatValue(myRoster.reduce((s, p) => s + p.player.value, 0))}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
